import { useMemo, useState } from 'react'
import QRCode from 'react-qr-code'
import { useHistory, useLocation } from 'react-router-dom'
import Location from '../../hooks/Location'
import useSettings from '../../hooks/Settings'
import useLetterTemplates from '../../hooks/useLetterTemplates'
import { useFirestoreGeneral } from '../../firebase/useFirestore'
import fillLetterTemplate from '../../helpers/letters/fillLetterTemplate'
import chooseLetterTemplate, { lettersFor, momentKindOf } from '../../helpers/letters/chooseLetterTemplate'
import { getQrcodePagePath, getQrcodeValue, parseLetterPath } from './qrcodeHelpers'
import isFlagOn from '../../helpers/isFlagOn'
import { letterIdentity } from '../../helpers/letters/mergeLetterTemplates'
import DEFAULT_LETTER_TEMPLATES from '../../data/defaultLetterTemplates'
import LetterEditor from '../../components/letters/LetterEditor'
import printIcon from '../../images/icons/print-icon.png'
import arrowLeftIcon from '../../images/icons/arrow-left-icon.png'

// AI-CONTEXT: See docs/features/adres-koppeling-algemene-qr.md

const KIND_LABELS = { voor: 'voormeting', na: 'nameting', output: 'output', storytelling: 'storytelling' }

/**
 * Printbare brief met de QR-code van een onderzoek of van een output
 * (registratieformulier van een activiteit): de standaardbrief, of een eigen
 * brief die Deccos voor deze organisatie klaarzette. Kop en tekst zijn aan te
 * passen (LetterEditor) en het voorbeeld toont wat er in de velden staat, ook
 * als het nog niet opgeslagen is: dat is wat er geprint wordt. Bereikt via 'Brief maken'
 * op de QR-pagina; de url-vorm staat in qrcodeHelpers (getLetterPath/parseLetterPath).
 */
const Letter = () => {
    const history = useHistory()
    // De instellingen die deze deellink uitzet (`?uit=…`), meegegeven door de QR-pagina.
    const { search } = useLocation()
    const path = parseLetterPath(Location())
    const { questionnaire, measureMomentID, researchID, route } = path
    const isOutput = route === 'event'
    const isResearch = route === 'research'

    const { compagnyName, logo } = useSettings()
    const { templates: allTemplates, loaded, error } = useLetterTemplates()
    const templates = useMemo(() => lettersFor(allTemplates, route), [allTemplates, route])
    // Alleen lezen wat bij deze soort brief hoort; '' slaat de query over.
    const moments = useFirestoreGeneral('MeasureMoments', 'ResearchID', (isResearch && researchID) || '')
    const researches = useFirestoreGeneral('Research', 'ID', (isResearch && researchID) || '')
    const eventForms = useFirestoreGeneral('EventForms', 'ID', (isOutput && questionnaire) || '')
    const activityID = Array.isArray(eventForms) ? eventForms[0]?.ActivityID || '' : ''
    const activities = useFirestoreGeneral('Activities', 'ID', activityID)

    // De keuze en het concept hangen aan de brief (sleutel + soort), niet aan het
    // document: na het opslaan wordt de standaardbrief een eigen document.
    const [chosen, setChosen] = useState('')
    const kind = isResearch ? momentKindOf(moments, measureMomentID) : (isOutput ? 'output' : route)
    const template = templates.find((t) => letterIdentity(t) === chosen) || chooseLetterTemplate(templates, kind)
    const identity = template ? letterIdentity(template) : ''

    // Een concept per brief: een andere brief kiezen toont die brief, terug
    // kiezen geeft het concept van de eerste terug — ook als je intussen de
    // tweede aanpaste. Afgeleid bij het renderen, niet gezet in een effect: een
    // snapshot kan een concept dus niet wissen.
    // AI-CONTEXT: See docs/pitfalls.md#state-draft-wiped-by-firestore-snapshot
    const [drafts, setDrafts] = useState(/** @type {Record<string, {Title: string, Body: string}>} */ ({}))
    const text = useMemo(() => drafts[identity]
        || { Title: template?.Title || '', Body: template?.Body || '' }, [drafts, identity, template])

    /** @param {React.ChangeEvent<HTMLSelectElement>} e */
    const templateChangeHandler = (e) => setChosen(e.target.value)

    /** @param {{Title: string, Body: string}} next */
    const draftChangeHandler = (next) => setDrafts((prev) => ({ ...prev, [identity]: next }))

    // Na terugzetten meteen de standaardtekst uit de app tonen, niet pas als de
    // snapshot de eigen versie heeft laten vallen.
    const showDefaultText = () => {
        const standard = DEFAULT_LETTER_TEMPLATES.find((t) => letterIdentity(t) === identity)
        draftChangeHandler({ Title: standard?.Title || '', Body: standard?.Body || '' })
    }

    const moment = Array.isArray(moments) ? moments.find((m) => m.ID === measureMomentID) : null
    const research = Array.isArray(researches) ? researches[0] : null
    const activity = Array.isArray(activities) ? activities[0] : null

    const letter = useMemo(() => fillLetterTemplate(template ? text : undefined, {
        organisatie: compagnyName || '',
        onderzoek: research?.Title || '',
        meetmoment: moment?.Title || '',
        // Alleen als het onderzoek echt eerst om het adres of de naam vraagt;
        // anders belooft de brief een stap die er niet is. De invulplek heet
        // `adresstap` omdat klaargezette eigen brieven die naam al dragen
        // (functions/letters/letterTemplates.js); hij staat voor 'de stap vóór
        // de vragen'. AI-CONTEXT: See docs/features/deelnemer-koppeling-algemene-link.md
        adresstap: isFlagOn(research?.AskAddressFirst)
            ? ' Je vult eerst je postcode en huisnummer in.'
            : isFlagOn(research?.AskNameFirst) ? ' Je vult eerst je voor- en achternaam in.' : '',
        activiteit: activity?.Activity || '',
    }), [template, text, compagnyName, research, moment, activity])

    // Meetmomenten en onderzoek (of formulier en activiteit) bepalen welke brief
    // past en vullen de titels in: zolang ze laden ('') geen brief, anders print
    // iemand een half ingevulde. Een formulier zonder activiteit wacht nergens op,
    // een storytelling-deellink op niets dan de brieven.
    const ready = loaded && (isOutput
        ? Array.isArray(eventForms) && (!activityID || Array.isArray(activities))
        : !isResearch || (Array.isArray(moments) && Array.isArray(researches)))
    // Een QR-code op papier die naar een verwijderd formulier wijst helpt niemand.
    const formMissing = isOutput && Array.isArray(eventForms) && eventForms.length === 0

    const link = getQrcodeValue({ ...path, search, host: window.location.hostname })

    const back = () => history.push(getQrcodePagePath({ ...path, search }))

    return (
        <div className="main">
            <div className="main-container">
                <div className="print-go-back-container" onClick={back}>
                    <img src={arrowLeftIcon} alt="" />
                    <p>Terug</p>
                </div>

                {!ready && <p>Laden…</p>}
                {ready && formMissing && (
                    <p className="letter-error" role="alert">Dit registratieformulier bestaat niet (meer).</p>
                )}
                {ready && !formMissing && (
                    <>
                        {error && (
                            <p className="letter-error" role="alert">
                                Je eigen brieven konden niet worden geladen. De standaardbrief staat wel klaar.
                            </p>
                        )}
                        <div className="letter-controls">
                            <label>
                                Sjabloon
                                <select value={identity} onChange={templateChangeHandler}>
                                    {templates.map((t) => (
                                        <option key={t.ID} value={letterIdentity(t)}>{`${t.Label} — ${KIND_LABELS[t.MomentKind] || t.MomentKind}`}</option>
                                    ))}
                                </select>
                            </label>
                            <div className="print-icon-container" onClick={() => window.print()}>
                                <img src={printIcon} alt="" className="print-icon" />
                                <p>Print brief</p>
                            </div>
                        </div>

                        {template && (
                            <LetterEditor
                                client={path.client}
                                template={template}
                                text={text}
                                onChange={draftChangeHandler}
                                onReset={showDefaultText}
                            />
                        )}

                        <article className="letter-page">
                            {logo && logo !== 'none' && (
                                <img className="letter-logo" src={logo} alt={`Logo ${compagnyName || ''}`.trim()} />
                            )}
                            {/* Een sjabloon mag zonder kop: dan ook geen lege kop. */}
                            {letter.title && <h1>{letter.title}</h1>}
                            {letter.paragraphs.map((lines, i) => (
                                <p key={i}>
                                    {lines.map((line, j) => (
                                        <span key={j}>{line}{j < lines.length - 1 && <br />}</span>
                                    ))}
                                </p>
                            ))}
                            <div className="letter-qr">
                                <QRCode value={link} size={180} />
                                <p className="letter-link">{link}</p>
                            </div>
                        </article>
                    </>
                )}
            </div>
        </div>
    )
}

export default Letter
